import { createContext, useContext } from 'react';
import type { Locale, MessageKey } from '../i18n/messages.ts';
import { randomName } from '../lib/names.ts';

export type ThemePreference = 'system' | 'light' | 'dark';
export type BoardTheme = 'ocean' | 'candy' | 'meadow' | 'walnut' | 'slate' | 'graphite';

export interface Preferences {
  /** Bumped when defaults change in a way saved preferences should pick up. */
  version: number;
  locale: Locale;
  theme: ThemePreference;
  boardTheme: BoardTheme;
  sound: boolean;
  coordinates: boolean;
  legalMoves: boolean;
  autoQueen: boolean;
  playerName: string;
}

export const BOARD_THEMES: Record<BoardTheme, { light: string; dark: string; lastMove: string }> = {
  ocean: { light: '#e3f1ff', dark: '#6aa5e8', lastMove: 'rgba(255, 213, 79, 0.6)' },
  candy: { light: '#ffe8f1', dark: '#e88fb8', lastMove: 'rgba(255, 213, 79, 0.65)' },
  meadow: { light: '#eef6d8', dark: '#77b25a', lastMove: 'rgba(255, 213, 79, 0.6)' },
  walnut: { light: '#eed7b4', dark: '#ae7f57', lastMove: 'rgba(222, 189, 56, 0.5)' },
  slate: { light: '#dfe4e8', dark: '#7f98aa', lastMove: 'rgba(130, 196, 230, 0.55)' },
  graphite: { light: '#d9d9d9', dark: '#8a8a8a', lastMove: 'rgba(240, 200, 90, 0.5)' },
};

const DEFAULT_BOARD_THEME: BoardTheme = 'ocean';

export const PREFERENCES_STORAGE_KEY = 'outchess:preferences';

export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language?.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

/** v2 = kid-friendly redesign (bright theme and colourful board by default). */
const PREFERENCES_VERSION = 2;

export const defaultPreferences = (): Preferences => ({
  version: PREFERENCES_VERSION,
  locale: detectLocale(),
  // Bright "day" look by default; the night look stays one tap away in settings.
  theme: 'light',
  boardTheme: DEFAULT_BOARD_THEME,
  sound: true,
  coordinates: true,
  legalMoves: true,
  autoQueen: false,
  playerName: '',
});

/** Merge saved preferences over the defaults, dropping values that no longer exist (e.g. a removed board theme). */
export function restorePreferences(saved: Partial<Preferences>, random: () => number = Math.random): Preferences {
  const prefs = { ...defaultPreferences(), ...saved };
  // Earlier versions saved their defaults even when nobody picked them, so move everyone to the new look once.
  if (saved.version !== PREFERENCES_VERSION) {
    prefs.version = PREFERENCES_VERSION;
    prefs.theme = 'light';
    prefs.boardTheme = DEFAULT_BOARD_THEME;
  }
  if (!(prefs.boardTheme in BOARD_THEMES)) prefs.boardTheme = DEFAULT_BOARD_THEME;
  if (!['system', 'light', 'dark'].includes(prefs.theme)) prefs.theme = 'light';
  // Start everyone with a fun nickname; they can change it on the friend page.
  if (!prefs.playerName.trim()) prefs.playerName = randomName(prefs.locale, random);
  return prefs;
}

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

export interface PreferencesContextValue {
  prefs: Preferences;
  update: (patch: Partial<Preferences>) => void;
  t: Translate;
}

export const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
}

export function useT(): Translate {
  return usePreferences().t;
}
